import i18n from '@/plugins/vue-i18n';
import { RouteRecordRaw } from 'vue-router';
import { Actions, Subjects } from '@boqnet/permissions';

const { t } = i18n.global;

export default [
  {
    path: '/:organisationId?/projects',
    name: 'projects',
    component: () => import('@/views/projects/Projects.vue'),
    meta: {
      title: t('routes.projects.projectsPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.READ,
      subject: Subjects.PROJECT,
    },
  },
  {
    path: '/:organisationId?/projects/:id?/create-site',
    name: 'create-site',
    component: () => import('@/views/projects/CreateSiteViewResolver.vue'),
    meta: {
      title: t('routes.projects.createSitePageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.CREATE,
      subject: Subjects.SITE,
    },
  },
  {
    path: '/:organisationId?/projects/:id?/create-project',
    name: 'create-project',
    component: () => import('@/views/projects/CreateProject.vue'),
    meta: {
      title: t('routes.projects.createProjectPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.CREATE,
      subject: Subjects.PROJECT,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/create-client-variation',
    name: 'create-client-variation',
    component: () => import('@/views/client/CreateClientVariation.vue'),
    meta: {
      title: t('routes.projects.createClientVariationPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.CREATE,
      subject: Subjects.CLIENT_VARIATION,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites',
    name: 'sites',
    component: () => import('@/views/projects/Sites.vue'),
    meta: {
      title: t('routes.projects.sitesPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.SITE,
    },
  },
  {
    path: '/:organisationId?/projects/:id/cashflow',
    name: 'project-cashflow',
    component: () => import('@/views/projects/Cashflow.vue'),
    meta: {
      title: t('routes.projects.projectCashflowPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.READ,
      subject: Subjects.PROJECT_CASHFLOW,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/:isClaimView?',
    component: () => import('@/views/projects/boq-view/BoqLayout.vue'),
    meta: {
      title: t('routes.projects.boqPageTitle'),
      layout: 'boq',
      requiresAuth: true,
    },
    beforeEnter: (to, from, next) => {
      const isLineItemRefreshed = sessionStorage.getItem('lineItemHistoryRefreshed') === 'true';

      to.meta.skipApiCalls = from.name === 'line-item-history' && !isLineItemRefreshed;

      next();
    },
    children: [
      // BOQ Tab
      {
        path: 'boq',
        name: 'boq',
        meta: {
          // Cost BOQ or Selling BOQ — professionals only ever get the selling view,
          // so a single READ:BOQ guard would lock them out of a page they may see.
          anyOf: [
            { action: Actions.READ, subject: Subjects.BOQ },
            { action: Actions.READ, subject: Subjects.SELLING_BOQ },
          ],
        },
        component: () => import('@/views/projects/boq-view/tabs/BoqModule.vue'),
      },
      // Orders Tab
      {
        path: 'orders',
        name: 'boq-orders',
        meta: {
          action: Actions.LIST,
          subject: Subjects.ORDER,
        },
        component: () => import('@/views/accounts/account-details-view/tabs/OrdersModule.vue'),
      },
      // Claims Tab
      {
        path: 'claims',
        name: 'boq-claims',
        meta: {
          action: Actions.LIST,
          subject: Subjects.ORDER,
        },
        component: () => import('@/views/accounts/account-details-view/tabs/ClaimsModule.vue'),
      },
      // Compliance Tab
      {
        path: 'compliance',
        name: 'boq-compliance',
        meta: {
          action: Actions.READ,
          subject: Subjects.COMPLIANCE,
        },
        component: () => import('@/views/projects/boq-view/tabs/ComplianceModule.vue'),
      },
      // Bank Draws Tab
      {
        path: 'bank-draws',
        name: 'boq-bank-draws',
        meta: {
          action: Actions.READ,
          subject: Subjects.BANK_DRAW,
        },
        component: () => import('@/views/projects/boq-view/tabs/BankDrawsModule.vue'),
      },
      {
        path: '',
        redirect: { name: 'boq' },
      },
    ],
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/line-item-history/:sectionId/item/:itemId/:group',
    name: 'line-item-history',
    component: () => import('@/views/projects/ItemOrderHistory.vue'),
    meta: {
      title: t('routes.projects.lineItemHistoryTitle'),
      layout: 'default',
      requiresAuth: true,
      // Cost BOQ or Selling BOQ — professionals only ever get the selling view,
      // so a single READ:BOQ guard would lock them out of a page they may see.
      anyOf: [
        { action: Actions.READ, subject: Subjects.BOQ },
        { action: Actions.READ, subject: Subjects.SELLING_BOQ },
      ],
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/cashflow',
    name: 'site-cashflow',
    component: () => import('@/views/projects/Cashflow.vue'),
    meta: {
      title: t('routes.projects.siteCashflowPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.READ,
      subject: Subjects.SITE_CASHFLOW,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/create-order-cart',
    name: 'boq-create-order-cart',
    component: () => import('@/views/projects/CreateOrderCart.vue'),
    meta: {
      title: t('routes.projects.boqCartPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.READ,
      subject: Subjects.CART,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/create-order-cart/confirm-requests',
    name: 'boq-confirm-order-requests',
    component: () => import('@/views/projects/CreateOrderRequests.vue'),
    meta: {
      title: t('routes.projects.confirmRequestsPageTitle'),
      layout: 'navigationOnly',
      requiresAuth: true,
      action: Actions.CREATE,
      subject: Subjects.ORDER,
    },
  },
  {
    path: '/:organisationId?/projects/:id/sites/:siteId/generate-claim-sheet/:drawId/:drawName',
    name: 'generate-claim-sheet',
    component: () => import('@/views/projects/ClaimSheet.vue'),
    meta: {
      title: t('routes.projects.claimSheetPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.READ,
      subject: Subjects.CLAIM_SHEET,
    },
  },
] as RouteRecordRaw[];
